import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './SectionProgressTree.module.css';

/**
 * SectionProgressTree (Slice 4 dot edition).
 *
 * Two-level collapsible navigation. Outer level is a parent group
 * (e.g., "About you"), inner level is a per-section row optionally
 * interleaved with non-collapsible sub-heading dividers.
 *
 * Retired per-section progress bars + answered/required
 * counters. Each section row now shows a single status dot:
 *   - ✓ green   complete (status === 'complete' && required > 0)
 *   - ● amber   required gaps remain (status === 'in_progress')
 *   - ○ gray    not started (status === 'not_started' && required > 0)
 *   - (none)    optional-only (required === 0)
 *
 * The parent header gets the same dot, computed by `resolveParentStatus`
 * over its descendant sections:
 *   - amber if any descendant is amber
 *   - else green if every descendant with required > 0 is complete
 *   - else gray if at least one descendant is gray (and none amber)
 *   - else null (every descendant is optional-only)
 *
 * Behavior:
 *   - One parent expanded at a time. Clicking a collapsed parent
 *     expands it; clicking the expanded one collapses it.
 *   - The parent containing `currentSectionId` auto-expands.
 *   - Sub-headings render as inline uppercase dividers.
 *
 * Accessibility:
 *   - `role="tree"`/`treeitem` semantics carry over from Slice 2.
 *   - Each dot is `aria-hidden="true"` and paired with a visually-
 *     hidden label ("Complete", "Required questions left", "Not
 *     started") so screen readers still announce the state.
 */

/** Section dot state derived from the existing engine progress view. */
export type TSectionDotState = 'complete' | 'gaps' | 'not_started';

export type TSectionStatus = 'not_started' | 'in_progress' | 'complete';

/**
 * Per-section view shape consumed by the tree. Re-exported because
 * the applicant-web and tests build the views and pass them in.
 *
 * `answered` / `required` / `percentComplete` are kept on the
 * interface to avoid touching the App.tsx wiring, but the tree no
 * longer renders them — the dot is derived purely from `status` +
 * `required`.
 */
export interface ISectionProgressView {
  /** Section id matching the catalog's `sectionId`. */
  id: string;
  /** Human-readable title rendered in the nav. */
  title: string;
  /** Count of visible+required fields with a clean answer. */
  answered: number;
  /** Total count of visible+required fields. */
  required: number;
  /** Integer percent complete in [0, 100]. */
  percentComplete: number;
  /** Section status from the engine's progress derivation. */
  status: TSectionStatus;
}

export interface IParentGroupSubHeadingView {
  label: string;
  sections: readonly ISectionProgressView[];
}

/**
 * Optional review/summary leaf rendered at the
 * end of an expanded parent's section list. Distinct row styling (a
 * decorative star prefix) makes it visually distinct from real
 * sections; otherwise it behaves like a section row (clickable,
 * keyboard-activatable, can be `aria-current="page"`). Summary leaves
 * never render a status dot — they're a destination, not a tracked
 * section.
 */
export interface IParentGroupSummaryLeafView {
  /** Pseudo-section id (e.g., `__review_about_you`). */
  id: string;
  /** Label rendered in the row (e.g., "Review your info"). */
  label: string;
}

export interface IParentGroupView {
  /** Matches `TParentGroupId` from the applicant-web intake module. */
  id: string;
  /** Human-readable parent label rendered in the header row. */
  label: string;
  /** Visual sub-groupings inside the parent (mutually exclusive
   *  with `sections`). */
  subHeadings?: readonly IParentGroupSubHeadingView[];
  /** Flat section list inside the parent (mutually exclusive with
   *  `subHeadings`). */
  sections?: readonly ISectionProgressView[];
  /**
   * Optional review/summary leaf rendered after the parent's section
   * list. When present, the leaf is the final entry inside the
   * expanded parent.
   */
  summaryLeaf?: IParentGroupSummaryLeafView;
}

export interface ISectionProgressTreeProps {
  parentGroups: readonly IParentGroupView[];
  currentSectionId: string | null;
  onSectionClick: (sectionId: string) => void;
  /** Optional ARIA label for the wrapping nav landmark. */
  ariaLabel?: string;
  /**
   * Section ids the user has actually navigated to. When supplied,
   * the tree suppresses the orange "gaps" dot for sections the user
   * hasn't visited yet — even if the engine reports them as
   * `in_progress` (which happens when the screener handoff seeds
   * answers into Immigration History / Eligibility before the user
   * reaches those sections). Unvisited sections show the empty
   * "not started" circle instead. When this prop is omitted, every
   * section is treated as visited (back-compat with the original
   * behavior).
   */
  visitedSectionIds?: ReadonlySet<string>;
}

const DOT_LABEL: Record<TSectionDotState, string> = {
  complete: 'Complete',
  gaps: 'Required questions left',
  not_started: 'Not started',
};

/**
 * Pure helper — maps an engine-driven section view onto the simpler
 * dot vocabulary the tree renders. Returns `null` when the section
 * has no required fields (optional-only sections show no icon, per
 * the spec).
 */
export function resolveSectionDot(
  view: ISectionProgressView,
  opts: { visited?: boolean } = {},
): TSectionDotState | null {
  if (view.required === 0) return null;
  switch (view.status) {
    case 'complete':
      return 'complete';
    case 'in_progress':
      // When `visited` is explicitly false, suppress the orange
      // "gaps" dot. The screener handoff seeds a few answers in
      // sections the user hasn't reached yet (e.g., Immigration
      // History, Eligibility), which flips engine status to
      // `in_progress`. Showing orange there on first login reads
      // as "you arrived broken" — wrong signal. Suppress to
      // `not_started` until the user actually navigates there.
      // When `visited` is omitted (legacy callers), behave as
      // before so existing call sites don't change.
      return opts.visited === false ? 'not_started' : 'gaps';
    case 'not_started':
      return 'not_started';
    default:
      return null;
  }
}

/**
 * Pure helper — rolls section dots up to the parent header. Walks
 * descendant sections in document order:
 *   - any `gaps` child → parent is `gaps`
 *   - else if every required-bearing child is `complete` → `complete`
 *   - else if at least one `not_started` child → `not_started`
 *   - else (all optional-only) → null
 */
export function resolveParentStatus(
  sections: readonly ISectionProgressView[],
  visitedSectionIds?: ReadonlySet<string>,
): TSectionDotState | null {
  let sawGaps = false;
  let sawNotStarted = false;
  let sawCompleteRequired = false;
  let sawAnyRequired = false;
  let sawIncomplete = false;

  for (const section of sections) {
    const visited =
      visitedSectionIds === undefined
        ? undefined
        : visitedSectionIds.has(section.id);
    const dot = resolveSectionDot(section, { visited });
    if (dot === null) continue;
    sawAnyRequired = true;
    if (dot === 'gaps') {
      sawGaps = true;
      sawIncomplete = true;
    } else if (dot === 'not_started') {
      sawNotStarted = true;
      sawIncomplete = true;
    } else if (dot === 'complete') {
      sawCompleteRequired = true;
    }
  }

  if (sawGaps) return 'gaps';
  if (!sawAnyRequired) return null;
  if (!sawIncomplete && sawCompleteRequired) return 'complete';
  if (sawNotStarted) return 'not_started';
  return 'complete';
}

/**
 * Walks all sections in a parent group, regardless of whether the
 * parent uses `subHeadings` or a flat `sections` list. Render and
 * focus order both follow this iteration order.
 */
function flattenParentSections(
  parent: IParentGroupView,
): readonly ISectionProgressView[] {
  if (parent.sections) return parent.sections;
  if (parent.subHeadings) {
    const out: ISectionProgressView[] = [];
    for (const sub of parent.subHeadings) {
      for (const s of sub.sections) out.push(s);
    }
    return out;
  }
  return [];
}

/**
 * Returns the parent index that owns `sectionId`, or `-1` if no
 * parent contains it.
 */
function findParentIndexForSection(
  parentGroups: readonly IParentGroupView[],
  sectionId: string | null,
): number {
  if (sectionId === null) return -1;
  for (let i = 0; i < parentGroups.length; i++) {
    for (const s of flattenParentSections(parentGroups[i])) {
      if (s.id === sectionId) return i;
    }
  }
  return -1;
}

export function SectionProgressTree({
  parentGroups,
  currentSectionId,
  onSectionClick,
  ariaLabel = 'Form sections',
  visitedSectionIds,
}: ISectionProgressTreeProps): JSX.Element {
  // Default expanded parent: the one owning currentSectionId, or the
  // first parent group when nothing is current.
  const initialExpandedIndex = useMemo(() => {
    const owning = findParentIndexForSection(parentGroups, currentSectionId);
    if (owning !== -1) return owning;
    return parentGroups.length > 0 ? 0 : -1;
  }, [parentGroups, currentSectionId]);

  const [expandedIndex, setExpandedIndex] = useState<number>(
    initialExpandedIndex,
  );

  // Auto-expand on navigation: if the new currentSectionId lives in a
  // parent that isn't currently expanded, switch to it.
  //
  // CRITICAL: this effect depends ONLY on `currentSectionId`. Earlier
  // versions also listed `parentGroups` in the dependency array, but
  // the parent App rebuilds that array on every render — even with
  // upstream memoization, anything that bumps the engine revision
  // (auto-save, cross-tab sync, visibility re-eval) cascades into a
  // new parentGroups reference and refires this effect, snapping
  // `expandedIndex` back to the parent owning currentSectionId on
  // every render. That fights the user's manual click on a different
  // parent ("expand → immediately collapse" — user-reported bug).
  //
  // Closing over `parentGroups` from render-time is safe: parent
  // structure doesn't change mid-session except across full route
  // remounts, which already remount this component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const owning = findParentIndexForSection(parentGroups, currentSectionId);
    if (owning !== -1 && owning !== expandedIndex) {
      setExpandedIndex(owning);
    }
  }, [currentSectionId]);

  const toggleParent = useCallback((index: number): void => {
    setExpandedIndex(prev => (prev === index ? -1 : index));
  }, []);

  // Refs to all focusable treeitems for keyboard navigation. We keep
  // a flat list in document order and lookup helpers to translate
  // between (parentIdx, sectionId) and the flat index.
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const setItemRef = useCallback(
    (key: string, el: HTMLButtonElement | null) => {
      if (el === null) itemRefs.current.delete(key);
      else itemRefs.current.set(key, el);
    },
    [],
  );

  // Build the visible treeitem key list in document order. Parent
  // headers always appear; section rows (and the optional summary
  // leaf at the end) appear only inside the currently expanded parent.
  const visibleKeys = useMemo<readonly string[]>(() => {
    const keys: string[] = [];
    for (let i = 0; i < parentGroups.length; i++) {
      keys.push(parentKey(i));
      if (i === expandedIndex) {
        for (const s of flattenParentSections(parentGroups[i])) {
          keys.push(sectionKey(i, s.id));
        }
        const leaf = parentGroups[i].summaryLeaf;
        if (leaf) {
          keys.push(sectionKey(i, leaf.id));
        }
      }
    }
    return keys;
  }, [parentGroups, expandedIndex]);

  const focusByKey = useCallback((key: string): void => {
    const el = itemRefs.current.get(key);
    if (el) el.focus();
  }, []);

  const onParentKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, parentIdx: number): void => {
      const isExpanded = parentIdx === expandedIndex;
      switch (e.key) {
        case 'Enter':
        case ' ': {
          e.preventDefault();
          toggleParent(parentIdx);
          return;
        }
        case 'ArrowRight': {
          if (!isExpanded) {
            e.preventDefault();
            setExpandedIndex(parentIdx);
          }
          return;
        }
        case 'ArrowLeft': {
          if (isExpanded) {
            e.preventDefault();
            setExpandedIndex(-1);
          }
          return;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const idx = visibleKeys.indexOf(parentKey(parentIdx));
          if (idx >= 0 && idx + 1 < visibleKeys.length) {
            focusByKey(visibleKeys[idx + 1]);
          }
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const idx = visibleKeys.indexOf(parentKey(parentIdx));
          if (idx > 0) focusByKey(visibleKeys[idx - 1]);
          return;
        }
        case 'Home': {
          e.preventDefault();
          if (visibleKeys.length > 0) focusByKey(visibleKeys[0]);
          return;
        }
        case 'End': {
          e.preventDefault();
          if (visibleKeys.length > 0)
            focusByKey(visibleKeys[visibleKeys.length - 1]);
          return;
        }
        default:
          return;
      }
    },
    [expandedIndex, focusByKey, toggleParent, visibleKeys],
  );

  const onSectionKeyDown = useCallback(
    (
      e: KeyboardEvent<HTMLButtonElement>,
      parentIdx: number,
      sectionId: string,
    ): void => {
      switch (e.key) {
        case 'Enter':
        case ' ': {
          e.preventDefault();
          onSectionClick(sectionId);
          return;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          focusByKey(parentKey(parentIdx));
          return;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const idx = visibleKeys.indexOf(sectionKey(parentIdx, sectionId));
          if (idx >= 0 && idx + 1 < visibleKeys.length) {
            focusByKey(visibleKeys[idx + 1]);
          }
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const idx = visibleKeys.indexOf(sectionKey(parentIdx, sectionId));
          if (idx > 0) focusByKey(visibleKeys[idx - 1]);
          return;
        }
        case 'Home': {
          e.preventDefault();
          if (visibleKeys.length > 0) focusByKey(visibleKeys[0]);
          return;
        }
        case 'End': {
          e.preventDefault();
          if (visibleKeys.length > 0)
            focusByKey(visibleKeys[visibleKeys.length - 1]);
          return;
        }
        default:
          return;
      }
    },
    [focusByKey, onSectionClick, visibleKeys],
  );

  return (
    <nav aria-label={ariaLabel}>
      <ul role="tree" className={styles.tree}>
        {parentGroups.map((parent, parentIdx) => {
          const isExpanded = parentIdx === expandedIndex;
          const parentClassName = isExpanded
            ? `${styles.tree__parent} ${styles['tree__parent--expanded']}`
            : styles.tree__parent;
          const chevronClassName = isExpanded
            ? `${styles.tree__chevron} ${styles['tree__chevron--expanded']}`
            : styles.tree__chevron;
          const flatSections = flattenParentSections(parent);
          const parentDot = resolveParentStatus(
            flatSections,
            visitedSectionIds,
          );
          const childListId = `tree-children-${parent.id}`;
          return (
            <li
              key={parent.id}
              role="none"
              className={styles.tree__parent_item}
            >
              <button
                type="button"
                role="treeitem"
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? childListId : undefined}
                className={parentClassName}
                ref={el => setItemRef(parentKey(parentIdx), el)}
                onClick={() => toggleParent(parentIdx)}
                onKeyDown={e => onParentKeyDown(e, parentIdx)}
              >
                <span aria-hidden="true" className={chevronClassName}>
                  ▶
                </span>
                <span className={styles.tree__parent_label}>
                  {parent.label}
                </span>
                <StatusDot dot={parentDot} />
              </button>
              {isExpanded ? (
                <ul
                  id={childListId}
                  role="group"
                  className={styles.tree__children}
                >
                  {parent.subHeadings
                    ? parent.subHeadings.flatMap(sub => [
                        // Render
                        // the sub-heading as a `role="presentation"`
                        // sibling of the section rows so the inner
                        // `role="group"` contains only treeitem `<li>`s.
                        // Previously this was wrapped in a `<li>` that
                        // also held a nested `<ul role="group">`,
                        // breaking the WAI-ARIA Tree pattern's
                        // group→treeitem nesting requirement.
                        <div
                          key={`${parent.id}::sub::${sub.label}`}
                          role="presentation"
                          className={styles.tree__subheading}
                        >
                          {sub.label}
                        </div>,
                        ...sub.sections.map(section => (
                          <SectionRow
                            key={section.id}
                            section={section}
                            parentIdx={parentIdx}
                            isActive={section.id === currentSectionId}
                            onSectionClick={onSectionClick}
                            onKeyDown={onSectionKeyDown}
                            setItemRef={setItemRef}
                            visited={
                              visitedSectionIds === undefined
                                ? undefined
                                : visitedSectionIds.has(section.id)
                            }
                          />
                        )),
                      ])
                    : (parent.sections ?? []).map(section => (
                        <SectionRow
                          key={section.id}
                          section={section}
                          parentIdx={parentIdx}
                          isActive={section.id === currentSectionId}
                          onSectionClick={onSectionClick}
                          onKeyDown={onSectionKeyDown}
                          setItemRef={setItemRef}
                          visited={
                            visitedSectionIds === undefined
                              ? undefined
                              : visitedSectionIds.has(section.id)
                          }
                        />
                      ))}
                  {parent.summaryLeaf ? (
                    <SummaryLeafRow
                      key={parent.summaryLeaf.id}
                      leaf={parent.summaryLeaf}
                      parentIdx={parentIdx}
                      isActive={parent.summaryLeaf.id === currentSectionId}
                      onSectionClick={onSectionClick}
                      onKeyDown={onSectionKeyDown}
                      setItemRef={setItemRef}
                    />
                  ) : null}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

interface IStatusDotProps {
  dot: TSectionDotState | null;
}

/**
 * Renders the dot + visually-hidden screen-reader label, or nothing
 * when `dot` is null (optional-only / no-required-fields case).
 *
 * The dot itself is decorative (`aria-hidden`); the SR label carries
 * the meaning so assistive tech still announces "Complete" / "Required
 * questions left" / "Not started" without leaning on color.
 */
function StatusDot({ dot }: IStatusDotProps): JSX.Element | null {
  if (dot === null) return null;
  const dotClassName = `${styles.tree__dot} ${styles[`tree__dot--${dot}`]}`;
  return (
    <>
      <span aria-hidden="true" className={dotClassName} />
      <span className={styles.visuallyHidden}>{DOT_LABEL[dot]}</span>
    </>
  );
}

interface ISectionRowProps {
  section: ISectionProgressView;
  parentIdx: number;
  isActive: boolean;
  onSectionClick: (sectionId: string) => void;
  onKeyDown: (
    e: KeyboardEvent<HTMLButtonElement>,
    parentIdx: number,
    sectionId: string,
  ) => void;
  setItemRef: (key: string, el: HTMLButtonElement | null) => void;
  /** Whether the user has navigated to this section. Threaded
   *  through from the parent tree's `visitedSectionIds` set. */
  visited?: boolean;
}

function SectionRow({
  section,
  parentIdx,
  isActive,
  onSectionClick,
  onKeyDown,
  setItemRef,
  visited,
}: ISectionRowProps): JSX.Element {
  const buttonClassName = isActive
    ? `${styles.tree__section} ${styles['tree__section--active']}`
    : styles.tree__section;
  const dot = resolveSectionDot(section, { visited });
  return (
    <li
      role="none"
      className={styles.tree__section_item}
      aria-current={isActive ? 'page' : undefined}
    >
      <button
        type="button"
        role="treeitem"
        className={buttonClassName}
        ref={el => setItemRef(sectionKey(parentIdx, section.id), el)}
        onClick={() => onSectionClick(section.id)}
        onKeyDown={e => onKeyDown(e, parentIdx, section.id)}
      >
        <span className={styles.tree__row}>
          <span className={styles.tree__title}>{section.title}</span>
          <StatusDot dot={dot} />
        </span>
      </button>
    </li>
  );
}

interface ISummaryLeafRowProps {
  leaf: IParentGroupSummaryLeafView;
  parentIdx: number;
  isActive: boolean;
  onSectionClick: (sectionId: string) => void;
  onKeyDown: (
    e: KeyboardEvent<HTMLButtonElement>,
    parentIdx: number,
    sectionId: string,
  ) => void;
  setItemRef: (key: string, el: HTMLButtonElement | null) => void;
}

/**
 * Review/summary leaf row. Distinct styling
 * (decorative star + italicized label) marks it as a non-section
 * destination; otherwise it routes through the same `onSectionClick`
 * + `onKeyDown` handlers as a SectionRow so keyboard nav and active-
 * row highlighting work without any special-casing in the host app.
 */
function SummaryLeafRow({
  leaf,
  parentIdx,
  isActive,
  onSectionClick,
  onKeyDown,
  setItemRef,
}: ISummaryLeafRowProps): JSX.Element {
  const buttonClassName = isActive
    ? `${styles.tree__section} ${styles['tree__section--active']} ${styles.tree__summary}`
    : `${styles.tree__section} ${styles.tree__summary}`;
  return (
    <li
      role="none"
      className={styles.tree__section_item}
      aria-current={isActive ? 'page' : undefined}
    >
      <button
        type="button"
        role="treeitem"
        className={buttonClassName}
        ref={el => setItemRef(sectionKey(parentIdx, leaf.id), el)}
        onClick={() => onSectionClick(leaf.id)}
        onKeyDown={e => onKeyDown(e, parentIdx, leaf.id)}
      >
        <span className={styles.tree__row}>
          <span className={styles.tree__title}>
            <span aria-hidden="true" className={styles.tree__summary_icon}>
              ★
            </span>
            <span className={styles.tree__summary_label}>{leaf.label}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

function parentKey(parentIdx: number): string {
  return `parent::${parentIdx}`;
}

function sectionKey(parentIdx: number, sectionId: string): string {
  return `section::${parentIdx}::${sectionId}`;
}
